import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import useUserContext from 'ecommerce/context/user';
import SignInWithAncestry from 'ecommerce/components/signInWithAncestry';
import useModalContext from 'components/context/modal';
import { Grid, List } from 'components/base';
import { H4, UL } from '@blueprintjs/core';
import {
  TREE_TYPE,
  getDefaultNumberOfGenerations,
  getFamilyTreeTypes,
  getNumbersOfGenerations,
} from 'editor-utils/template';
import uniq from 'utils/uniq';
import { formatSeconds } from 'utils/format';

import ancestorsApi from '../../api/ancestors';
import generateAncestryData from '../../generator/generateAncestryData';

import { Layout, Infobox } from '../../components/rich';
import useEditorItemContext from '../../context/editorItemContext';
import { Button, Panel, Typography } from '../../components/base';

import EditorLayout from '../components/layout';
import useEditorContext from '../../context/editorContext';
import CoupleSelectTabs from '../../contentWizard/people/couples/coupleSelectTabs';

import FamilyTreeSelect from '../../components/ancestry/familyTreeSelect';
import TreeTypeSelect from '../../components/ancestry/treeTypeSelect';
import NumberOfGenerationsSelect from '../../components/ancestry/numberOfGenerationsSelect';
import AncestryWebSocketSynchronizer from '../components/ancestryWebSocketSynchronizer';

const AncestrySource = props => {
  const userContext = useUserContext();
  const modalContext = useModalContext();
  const editorContext = useEditorContext();
  const { product, template, config } = useEditorItemContext();

  const [authorized, setAuthorized] = useState(true);

  const [trees, setTrees] = useState();
  const [selectedTree, setSelectedTree] = useState();

  const [ancestryValue, setAncestryValue] = useState({});
  const [ancestryError, setAncestryError] = useState();

  const treeTypes = useMemo(() => getFamilyTreeTypes(template), [template]);
  const [selectedTreeType, setSelectedTreeType] = useState(treeTypes[0]);

  const isPartnerRequired = useMemo(() => selectedTreeType === TREE_TYPE.COMBO, [selectedTreeType]);

  const numbersOfGenerations = useMemo(
    () => getNumbersOfGenerations(template, config.numbersOfGenerations, selectedTreeType),
    [template, config, selectedTreeType],
  );
  const [selectedNumberOfGenerations, setSelectedNumberOfGenerations] = useState();

  const progressLabelRef = useRef();

  const [progressLabel, setProgressLabel] = useState("We're turning your data from Ancestry.com into a project preview.");

  useEffect(() => {
    setSelectedNumberOfGenerations(value => {
      if (numbersOfGenerations.indexOf(value) < 0) {
        return getDefaultNumberOfGenerations(
          template,
          config.numbersOfGenerations,
          selectedTreeType,
        );
      }
      return value;
    });
  }, [template, config, numbersOfGenerations, selectedTreeType]);

  const isPreviewEnabled = useMemo(() => {
    if (!ancestryValue.treeId) {
      return false;
    }
    if (!ancestryValue.personId) {
      return false;
    }
    if (isPartnerRequired && !ancestryValue.partnerId) {
      return false;
    }
    return true;
  }, [ancestryValue, isPartnerRequired]);

  const handleOnAncestryError = error => {
    setSelectedTree();
    setAncestryError(error);

    if (error.code === 401) {
      setAuthorized(false);
      setTrees([]);
    } else {
      editorContext.trackEvent('ancestry_data_error');
    }
  };

  useEffect(() => {
    (async () => {
      const { error: ancestryTokenError } = await userContext.ancestryAuth.getToken();
      if (ancestryTokenError) {
        handleOnAncestryError(ancestryTokenError);
        return;
      }

      const { data, error } = await ancestorsApi("ancestry", editorContext.accessToken).getTrees();
      if (error) {
        handleOnAncestryError(error);
        return;
      }

      setAuthorized(true);
      setTrees(data);
      // When user has only one tree, select it automatically
      if (data.length === 1) {
        setSelectedTree(data[0]);
      }
    })();
  }, [editorContext.accessToken]);

  const handleOnPreviewClick = async () => {
    const { treeId, treeName, personId, partnerId } = ancestryValue;

    if (!treeId || !personId) {
      await props.onSuccess(() => ({
        error: 'Please select person',
      }));
      return;
    }

    const { error: ancestryTokenError } = await userContext.ancestryAuth.getToken();
    if (ancestryTokenError) {
      handleOnAncestryError(ancestryTokenError);
      return;
    }

    progressLabelRef.current = undefined;
    modalContext.show(
      <AncestryWebSocketSynchronizer
        ancestryAuth={userContext.ancestryAuth}
        accessToken={editorContext.accessToken}
        productId={product.id}
        persons={[personId, partnerId]}
        numberOfGenerations={selectedNumberOfGenerations}
        treeName={treeName}
        treeType={selectedTreeType}
        checkSyncStatusFirst
        initialProgressLabel={"We're turning your data from Ancestry.com into a project preview."}
        onDelay={setProgressLabel => {
          progressLabelRef.current = setProgressLabel;
        }}
        onSuccess={async ({ onProgress }) => {
          const { data, error, session } = await generateAncestryData(
            editorContext.accessToken,
            template,
            userContext.ancestryAuth,
            {
              type: "ancestry",
              treeId,
              treeName,
              treeType: selectedTreeType,
              personId,
              partnerId,
              numberOfGenerations: selectedNumberOfGenerations,
            },
            [],
            ({ pending, total }) => {
              onProgress(((total - pending) / total) * 100);
            },
            (delayInSeconds) => {
              if (progressLabelRef.current) {
                progressLabelRef.current(`This is taking a bit longer than expected - about ${formatSeconds(delayInSeconds)} more to go.`);
              }
              console.log(`Sync is delayed by ${delayInSeconds} seconds`);
            }
          );

          if (error) {
            editorContext.trackEvent('ancestry_generator_error');
          }

          props.onSuccess(() => ({ data, error, session }));
        }}
      />,
      {
        title: 'Building your project',
        closeable: false,
      },
    );
  };

  const handleOnSelectTree = tree => {
    setAncestryError();
    setSelectedTree(tree);
  };

  let children;
  if (ancestryError && ancestryError.code !== 401) {
    // ANY POSSIBLE ERROR
    children = (
      <>
        <Layout.Container>
          <Infobox type="warning" mb={3}>
            <Typography.Heading size={3} mb={1}>
              There is an issue with collecting Ancestry data
            </Typography.Heading>
            <Typography.Paragraph>
              We need to fix the issue. Please try to Sign in with your Ancestry account again.
              <br />
              <br />
              <strong>Error Details</strong>
            </Typography.Paragraph>
            <pre>{JSON.stringify(ancestryError, null, 2)}</pre>
          </Infobox>
        </Layout.Container>

        <SignInWithAncestry redirectUrl="referer" className="text-center" />
      </>
    );
  } else if (!trees) {
    // LOADING FAMILY TREES
    children = null;
  } else if (!authorized) {
    // USER HAS NO ANCESTRY TOKEN
    children = (
      <>
        <Layout.Container>
          <Infobox mb={3}>
            <Typography.Heading size={3} mb={1}>
              Your account does not have access to Ancestry
            </Typography.Heading>
            <Typography.Paragraph>
              We need the data from you Ancestry account.
              <br />
              Please use button below to Sign in with your Ancestry account.
            </Typography.Paragraph>
          </Infobox>
        </Layout.Container>

        <SignInWithAncestry redirectUrl="referer" className="text-center" />
      </>
    );
  } else if (trees.length === 0) {
    // USER IS AUTHORIZED, BUT HAS NO FAMILY TREES
    children = (
      <>
        <Layout.Container>
          <Infobox mb={3}>
            <Typography.Heading size={3} mb={1}>
              No Ancestry Trees
            </Typography.Heading>
            <Typography.Paragraph>
              You don&apos;t have any family tree in your Ancestry account
            </Typography.Paragraph>
          </Infobox>
        </Layout.Container>
      </>
    );
  } else {
    // EDITOR IS READY TO START WITH SELECTING FAMILY TREE
    children = (
      <>
        {treeTypes.length > 1 && (
          <Panel borderColor="orange" className="mb-3">
            <H4>Select a {config.printType ?? 'tree'} type</H4>
            <TreeTypeSelect
              treeTypes={uniq([...treeTypes, TREE_TYPE.DESC])}
              fill
              selectedTreeType={selectedTreeType}
              onChange={setSelectedTreeType}
            />

            <H4 className="mt-3">All books include the ability to...</H4>
            <List styled>
              <List.Item>
                Include templates for family group sheets, timelines, and record pages that fill
                automatically with information from your Ancestry account.
              </List.Item>
              <List.Item>
                Add more information to timelines, and import Ancestry census records and photos.
              </List.Item>
              <List.Item>Insert additional pages to include more generations.</List.Item>
              <List.Item>
                Decorate with embellishments, decorative quotes, and backgrounds. Choose from the
                hundreds we provide, or upload your own!
              </List.Item>
              <List.Item>
                Add your personal pictures and text to tell the story of your family.
              </List.Item>
            </List>
          </Panel>
        )}

        <Panel borderColor="green">
          <H4>Select a family tree</H4>
          <Grid.Row>
            <Grid.Col xs={12} sm={6} className="mw-300">
              <FamilyTreeSelect
                trees={trees}
                fill
                selectedTree={selectedTree}
                onChange={handleOnSelectTree}
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6} className="d-flex align-items-center">
              <Typography.Paragraph mb={0}>
                After you pick the family tree, we start collecting all available data from
                Ancestry.
              </Typography.Paragraph>
            </Grid.Col>
          </Grid.Row>

          {numbersOfGenerations.length > 1 && (
            <>
              <H4 className="mt-3">Select the number of generations</H4>
              <Grid.Row>
                <Grid.Col xs={12} sm={6} className="mw-300">
                  <NumberOfGenerationsSelect
                    fill
                    values={numbersOfGenerations}
                    selectedValue={selectedNumberOfGenerations}
                    onChange={setSelectedNumberOfGenerations}
                  />
                </Grid.Col>
                <Grid.Col xs={12} sm={6} className="d-flex align-items-center">
                  <Typography.Paragraph mb={0}>
                    Select the number of generations you want included in your creation.
                  </Typography.Paragraph>
                </Grid.Col>
              </Grid.Row>
            </>
          )}

          {selectedTree && (
            <React.Fragment key={selectedTree.id}>
              <H4 className="mt-3">Select a starting person</H4>
              <CoupleSelectTabs
                key={selectedTree.id}
                onSelect={event => {
                  setAncestryValue({
                    treeId: selectedTree.id,
                    treeName: selectedTree.name,
                    personId: event.person?.id,
                    partnerId: event.partner?.id,
                  });
                }}
                treeId={selectedTree.id}
                partnerRequired={isPartnerRequired}
              />

              <div className="text-right">
                <Button
                  large
                  variant="primary"
                  disabled={!isPreviewEnabled}
                  onClick={handleOnPreviewClick}
                >
                  Preview your creation
                </Button>
              </div>
            </React.Fragment>
          )}
        </Panel>
      </>
    );
  }

  return (
    <EditorLayout
      actionButton={
        <Button variant="primary" disabled={!isPreviewEnabled} onClick={handleOnPreviewClick}>
          Preview
        </Button>
      }
      primaryChildren={props.children}
    >
      <div className="mw-1280 mx-auto">{children}</div>
    </EditorLayout>
  );
};

AncestrySource.propTypes = {
  onSuccess: PropTypes.func,
  children: PropTypes.node,
};

export default AncestrySource;
